import React from "react";
import { createContext, useContext } from "react";
import { FormInstance } from "antd/lib/form";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { v4 as uuidv4 } from "uuid";
import { DEFAULT_SIGNIFICANCE_LEVEL } from "@src/pages/ab-test-explore/contanst";
import { useParsed } from "@refinedev/core";
import { SupportedModelAlias } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { Form } from "antd";
import { MEASURE_EVENT_COUNT } from "@src/pages/ab-test-explore/component/charts/metric-performance/component/metric-performance-item/metric-measure";
import { trackEventPayload } from "@src/util/track-event-payload";
import { useTracking } from "react-tracking";
import { useGetEventParams } from "@src/hooks/use-get-event-params";
import { parseEventParam } from "@src/helpers/text-helper";
import { OperatorManager } from "@src/util/ab-testing/operator-manager";

type MetricPerformanceContextType = {
    activeKey: string | undefined;
    setActiveKey: React.Dispatch<React.SetStateAction<string | undefined>>;
    form: FormInstance<any>;
    getValueTabItemOfTargetKey: (targetKey: string) => any;
    handleUpdateMetricTabItems: (targetKey: string, fieldToUpdate: string | string[]) => void;
    removeMetricTabItems: (targetKey: string, fieldToUpdate: string) => void;
    add: (_value?: Record<string, any>, _label?: string) => void;
    remove: (targetKey: TargetKey) => void;
    firebaseEventParams: { param_key: string; param_type: string }[] | null;
    targetedModel: SupportedModelAlias;
    isRequiredEventNameInFilter: () => boolean;
    getEventParamType: (paramKey: string) => string | undefined;
};
export const MetricPerformanceContext = createContext({} as MetricPerformanceContextType);

type IProps = {
    children: React.ReactNode;
    form: FormInstance<any>;
};

export type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

export const generateKey = () => uuidv4();

export const MetricPerformanceContextProvider: React.FC<IProps> = ({ children, form }) => {
    const params = useParsed();

    const { trackEvent } = useTracking();
    const tabIndex = React.useRef(0);
    const [activeKey, setActiveKey] = React.useState<string>();
    const generateLabel = () => {
        tabIndex.current += 1;
        return `Metric ${tabIndex.current}`;
    };

    const metricTabItems = Form.useWatch(ChartFormFieldsEnum.METRIC_TAB_ITEMS);
    const targetedModel = metricTabItems?.find((item: any) => item.key === activeKey)?.[
        ChartFormFieldsEnum.METRIC_TARGET_MODEL
    ];

    const { firebaseEventParams, getEventParamType } = useGetEventParams({
        enabled: targetedModel === SupportedModelAlias.FIREBASE,
    });

    const getValueTabItemOfTargetKey = React.useCallback(
        (targetKey: string) => {
            const metricTabItems = form.getFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS) ?? [];
            return metricTabItems.find((item: any) => item.key === targetKey);
        },
        [form],
    );

    const handleUpdateMetricTabItems = React.useCallback(
        (targetKey: string, _fieldToUpdate: string | string[]) => {
            const metricTabItem = form.getFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS) ?? [];

            const newMetricItem = metricTabItem.map((item: any) => {
                if (item.key !== targetKey) return item;
                const fieldToUpdate = typeof _fieldToUpdate === "string" ? [_fieldToUpdate] : _fieldToUpdate;
                const _item = item;
                fieldToUpdate.forEach((field) => {
                    _item[field] = form.getFieldValue([targetKey, field]);
                });
                return _item;
            });

            form.setFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS, newMetricItem);
        },
        [form],
    );

    const removeMetricTabItems = React.useCallback(
        (targetKey: string, fieldToUpdate: string) => {
            const metricTabItem = form.getFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS) ?? [];

            const newMetricItem = metricTabItem.map((item: any) => {
                if (item.key !== targetKey) return item;
                delete item[fieldToUpdate];
                return item;
            });

            form.setFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS, newMetricItem);
        },
        [form],
    );

    const add = React.useCallback(
        (_value?: Record<string, any>, _label?: string) => {
            const metricItems = form.getFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS) ?? [];
            const newActiveKey = generateKey();
            const tabLabel = _label ? `${_label} Copy` : generateLabel();
            const value = _value
                ? {
                      ..._value,
                      [ChartFormFieldsEnum.METRIC_TAB_NAME]: tabLabel,
                  }
                : {
                      [ChartFormFieldsEnum.METRIC_TAB_NAME]: tabLabel,
                      [ChartFormFieldsEnum.METRIC_SIGNIFICANCE_LEVEL]: DEFAULT_SIGNIFICANCE_LEVEL,
                  };

            form.setFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS, [
                ...metricItems,
                {
                    key: newActiveKey,
                    ...value,
                },
            ]);
            form.setFieldValue(newActiveKey, value);
            trackEvent(trackEventPayload.metricTabButtonClick("add", tabLabel));
            setActiveKey(newActiveKey);
        },
        [form],
    );

    const remove = (targetKey: TargetKey) => {
        const metricItems = form.getFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS) ?? [];
        const targetIndex = metricItems.findIndex((pane: any) => pane.key === targetKey);
        const newPanes = metricItems.filter((pane: any) => pane.key !== targetKey);
        if (newPanes.length && targetKey === activeKey) {
            const { key } = newPanes[targetIndex === newPanes.length ? targetIndex - 1 : targetIndex];
            setActiveKey(key);
        }
        form.setFieldValue(ChartFormFieldsEnum.METRIC_TAB_ITEMS, newPanes);
    };

    const hasInitTabIndex = React.useRef(false);
    const metricTabItemWatch = Form.useWatch(ChartFormFieldsEnum.METRIC_TAB_ITEMS);

    const isSyncEventParam = React.useRef(false);
    React.useEffect(() => {
        if (!metricTabItemWatch || firebaseEventParams?.length === 0 || isSyncEventParam.current) return;
        function syncParamTypeIntoParamName(eventParam?: string) {
            if (!eventParam || !eventParam.startsWith("ep_"))
                return {
                    name: eventParam,
                    param_key: null,
                    param_type: null,
                };
            const paramKey = parseEventParam(eventParam).param_key;
            const paramType = getEventParamType(paramKey) ?? "string";
            return {
                name: `ep_${paramType}_${paramKey}`,
                param_key: paramKey,
                param_type: paramType,
            };
        }
        metricTabItemWatch.forEach((item: any) => {
            const metricBreakdown = item[ChartFormFieldsEnum.METRIC_BREAKDOWN];
            const metricFilter = item[ChartFormFieldsEnum.METRIC_FILTER];
            const metricBreakdownWithParamType = metricBreakdown?.map((eventParam: string) => {
                return syncParamTypeIntoParamName(eventParam).name;
            });
            const metricFilterWithParamType = metricFilter
                ? {
                      ...metricFilter,
                      rules: metricFilter?.rules?.map((rule: any) => {
                          const { name, param_type } = syncParamTypeIntoParamName(rule?.field);
                          return {
                              ...rule,
                              field: name,
                              operator: rule?.operator
                                  ? OperatorManager.convertSymbolToName(rule?.operator)
                                  : rule?.operator,
                              value: Array.isArray(rule?.value)
                                  ? rule?.value?.map((value: any) => (param_type === "number" ? Number(value) : value))
                                  : param_type === "number"
                                  ? [Number(rule?.value)]
                                  : rule?.value,
                          };
                      }),
                  }
                : metricFilter;
            form.setFieldValue([item.key, ChartFormFieldsEnum.METRIC_BREAKDOWN], metricBreakdownWithParamType);
            form.setFieldValue([item.key, ChartFormFieldsEnum.METRIC_FILTER], metricFilterWithParamType);
        });
        isSyncEventParam.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify([firebaseEventParams, metricTabItemWatch, isSyncEventParam])]);
    React.useEffect(() => {
        if (hasInitTabIndex.current) return;
        if (metricTabItemWatch && metricTabItemWatch.length > 0) {
            tabIndex.current = metricTabItemWatch.length;
            hasInitTabIndex.current = true;
        }
    }, [metricTabItemWatch, hasInitTabIndex]);

    React.useEffect(() => {
        if (params.id) return;
        const timeoutId = setTimeout(() => {
            add();
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [form, add, params.id]);

    const isRequiredEventNameInFilter = () => {
        const targetModal = form.getFieldValue([activeKey, ChartFormFieldsEnum.METRIC_TARGET_MODEL]);
        const metricMeasure = form.getFieldValue([activeKey, ChartFormFieldsEnum.METRIC_MEASURE]);
        return targetModal === SupportedModelAlias.FIREBASE && metricMeasure === MEASURE_EVENT_COUNT;
    };

    const values = {
        activeKey,
        setActiveKey,
        form,
        firebaseEventParams,
        targetedModel,
        getValueTabItemOfTargetKey,
        removeMetricTabItems,
        handleUpdateMetricTabItems,
        add,
        remove,
        isRequiredEventNameInFilter,
        getEventParamType,
    };
    return <MetricPerformanceContext.Provider value={values}>{children}</MetricPerformanceContext.Provider>;
};

export const useMetricPerformanceContext = () => useContext(MetricPerformanceContext);
